<template>
    <div class="lightspeed_configuration">
        <!-- <div v-if="error.status" class="error">
            <h4 class="headline3 error__headline">Error {{error.status}}</h4>
            {{error.message}}
        </div> -->
        <div
            class="lightspeed_configuration__form"
            v-if="!shopSettingsLoading && storeShopSettings"
        >
            <Formfield
                label="Authentication Settings"
                helpText="You can find your API credentials in your Feedback Company
                    My account page or by contacting helpdesk@feedbackcompany.com"
                :required="true"
            >
                <TextInput
                    :value="storeShopSettings.credentials.client_id"
                    @input="userInput"
                    name="credentials.client_id"
                    label="Client ID"
                    :valueMaxLength="32"
                    :required="true"
                />
                <TextInput
                    :value="storeShopSettings.credentials.client_secret"
                    @input="userInput"
                    name="credentials.client_secret"
                    label="Client Secret"
                    :valueMaxLength="32"
                    :required="true"
                />
            </Formfield>
            <RadioQuestion
                label="Invitation settings"
                :value="storeShopSettings.invitation.order_status"
                @input="userInput"
                helpText="
                    Configure which order status should trigger a review invitation."
                name="invitation.order_status"
                :required="true"
                :options="[
                    {
                        displayValue: 'When order is paid',
                        value: 'paid',
                    }, {
                        displayValue: 'When order is completed (paid & shipped)',
                        value: 'shipped',
                    },
                ]"
            />
            <AmountOfUnitPicker
                :value="storeShopSettings.invitation.delay"
                @input="userInput"
                name="invitation.delay"
                label="Invitation delay"
                helpText="Set the delay before sending the invitation
                    (measured from when we receive the trigger)."
                :required="true"
                textInputLabel="Delay Amount"
                dropdownPlaceholder="Time Units"
                :units="timeUnits"
            />
            <ValidationMessage
                :validationMessage="validationRules.invitationDelaySmallerThan100Days().message"
                :displayInvalid="!validationRules.invitationDelaySmallerThan100Days().condition"
            />
            <Formfield
                label="Send Reminder"
                helpText="Enable to send a reminder."
                :required="true"
            >
                <SwitchInput
                    :value="storeShopSettings.invitation.has_reminder"
                    name="invitation.has_reminder"
                    @input="userInput"
                />
            </Formfield>
            <AmountOfUnitPicker
                v-if="storeShopSettings.invitation.has_reminder"
                :value="storeShopSettings.invitation.reminder"
                :required="true"
                @input="userInput"
                name="invitation.reminder"
                label="Reminder delay"
                helpText="Set the delay before sending the reminder
                    (measured from when we receive the trigger)."
                textInputLabel="Delay Amount"
                dropdownPlaceholder="Time Units"
                :units="timeUnits"
                ref="reminderDelayInput"
            />
            <ValidationMessage
                :validationMessage="validationRules.inviteDelaySmallerThanReminderDelay().message"
                :displayInvalid="
                    validationRules.inviteDelaySmallerThanReminderDelay().isShowing()
                    && !validationRules.inviteDelaySmallerThanReminderDelay().condition
                "
            />
            <ValidationMessage
                :validationMessage="validationRules.reminderDelaySmallerThan100Days().message"
                :required="true"
                :displayInvalid="!validationRules.reminderDelaySmallerThan100Days().condition"
            />
            <section class="widgets">
                <h3 class="headline4">Widget Settings</h3>
                <Formfield
                    label="Enable Product Review Widgets"
                    :required="true"
                >
                    <SwitchInput
                        :value="storeShopSettings.widgets.enabled"
                        name="widgets.enabled"
                        @input="userInput"
                    />
                </Formfield>
                <Formfield
                    label="Extended Product Review Widget Type"
                    v-if="storeShopSettings.widgets.enabled"
                    :required="true"
                >
                    <SegmentedControl
                        :value="storeShopSettings.widgets.product_extended.type"
                        name="widgets.product_extended.type"
                        @input="userInput"
                        :required="true"
                        :options="[
                            {
                                displayValue: 'Sidebar',
                                value: 'sidebar',
                            }, {
                                displayValue: 'Popup',
                                value: 'popup',
                            },
                        ]"
                    />
                    <WidgetExample
                        class="lightspeed_configuration__widget-example"
                        :state="storeShopSettings.widgets.product_extended.type"
                    />
                </Formfield>
                <Formfield
                    label="Advanced Summary Product Review Widget Settings"
                    v-if="storeShopSettings.widgets.enabled"
                    helpText="
                        Define a CSS selector after which the summary widget will be placed.
                        Default is the first h1 on the page"
                    :required="false"
                >
                    <TextInput
                        name="widgets.product_summary.detail_selector"
                        label="CSS Selector"
                        :value="storeShopSettings.widgets.product_summary.detail_selector"
                        :required="false"
                        @input="userInput"
                        :valueMaxLength="500"
                        :displayValueMaxLength="true"
                    />
                </Formfield>
            </section>
        </div>
    </div>
</template>

<script>
import {
    TextInput,
    RadioQuestion,
    AmountOfUnitPicker,
    ValidationMessage,
    SwitchInput,
    Formfield,
    SegmentedControl,
    FormMixin,
} from 'feedback-company-vue-components';

import WidgetExample from '@/components/atoms/WidgetExample.vue';

export default {
    name: 'LightspeedConfiguration',
    mixins: [FormMixin],
    components: {
        TextInput,
        RadioQuestion,
        AmountOfUnitPicker,
        ValidationMessage,
        SwitchInput,
        Formfield,
        WidgetExample,
        SegmentedControl,
    },
    data() {
        return {
            orderStatus: {
                displayValue: 'When order is paid',
                value: 'paid',
            },
            timeUnits: [
                {
                    displayValue: 'Hours',
                    value: 'hours',
                },
                {
                    displayValue: 'Days',
                    value: 'days',
                },
            ],
            secondsUnit: {
                hours: 3600,
                days: 86400,
                weeks: 604800,
            },
            validationRules: {
                inviteDelaySmallerThanReminderDelay: () => ({
                    condition:
                        (this.storeShopSettings
                        && this.storeShopSettings.invitation
                        && !this.storeShopSettings.invitation.has_reminder)
                        || this.inviteDelaySmallerThanReminderDelay,
                    message:
                        'There should be atleast 24 hours between your invite and your reminder.',
                    isShowing: () => this.storeShopSettings.invitation.delay.amount
                            !== null
                        && this.storeShopSettings.invitation.reminder
                            .amount !== null
                        && this.storeShopSettings.invitation.delay.unit
                            !== null
                        && this.storeShopSettings.invitation.reminder
                            .unit !== null,
                }),
                reminderDelaySmallerThan100Days: () => ({
                    condition:
                        (this.storeShopSettings
                        && this.storeShopSettings.invitation
                        && !this.storeShopSettings.invitation.has_reminder)
                        || this.reminderInSeconds <= 8553600,
                    message:
                        'The Reminder delay should be smaller than 100 days',
                    isShowing: () => this.storeShopSettings.invitation.delay.amount
                            !== null
                            && this.storeShopSettings.invitation.reminder
                                .amount !== null
                            && this.storeShopSettings.invitation.delay
                                .unit !== null
                            && this.storeShopSettings.invitation.reminder
                                .unit !== null,
                }),
                invitationDelaySmallerThan100Days: () => ({
                    condition: this.invitationInSeconds <= 8553600,
                    message: 'The Invitation delay should be smaller than 100',
                    isShowing: () => this.storeShopSettings.invitation.delay.amount
                            !== null
                            && this.storeShopSettings.invitation.reminder
                                .amount !== null
                            && this.storeShopSettings.invitation.delay
                                .unit !== null
                            && this.storeShopSettings.invitation.reminder
                                .unit !== null,
                }),
            },
        };
    },
    computed: {
        storeShopSettings() {
            return this.$store.state.shopSettings;
        },
        currentLanguage() {
            return this.$store.getters.currentLanguage;
        },
        shopSettingsLoading() {
            return this.$store.state.shopSettingsLoading;
        },
        invitationInSeconds() {
            if (
                !this.storeShopSettings.invitation
                || !this.storeShopSettings.invitation.reminder.amount
                || !this.storeShopSettings.invitation.reminder.unit
            ) return false;
            return (
                this.storeShopSettings.invitation.delay.amount
                * this.secondsUnit[
                    this.storeShopSettings.invitation.delay.unit
                ]
            );
        },
        reminderInSeconds() {
            if (
                !this.storeShopSettings.invitation
                || !this.storeShopSettings.invitation.reminder.amount
                || !this.storeShopSettings.invitation.reminder.unit
            ) {
                return false;
            }
            return (
                this.storeShopSettings.invitation.reminder.amount
                * this.secondsUnit[
                    this.storeShopSettings.invitation.reminder.unit
                ]
            );
        },
        inviteDelaySmallerThanReminderDelay() {
            if (
                !this.storeShopSettings.invitation
                || (
                    this.storeShopSettings.invitation.delay === {}
                    || this.storeShopSettings.invitation.reminder === {}
                    || !this.storeShopSettings.invitation.delay.amount
                    || !this.storeShopSettings.invitation.reminder.amount
                    || !this.storeShopSettings.invitation.delay.unit
                    || !this.storeShopSettings.invitation.reminder.unit
                )
            ) {
                return false;
            }
            const inviteInSeconds = this.storeShopSettings.invitation.delay.amount
                * this.secondsUnit[
                    this.storeShopSettings.invitation.delay.unit
                ];
            const reminderInSeconds = this.storeShopSettings.invitation.reminder.amount
                * this.secondsUnit[
                    this.storeShopSettings.invitation.reminder.unit
                ];
            const minimumGap = this.secondsUnit.hours * 24;
            return reminderInSeconds - inviteInSeconds >= minimumGap;
        },
    },
    watch: {
        isValid(valid) {
            this.$store.commit('setFormValid', { formValid: valid });
        },
    },
    methods: {
        userInput(value, input) {
            this.$store.commit('setAnswer', {
                value,
                name: input.$attrs.name,
            });
        },
    },
    mounted() {
        this.$store.commit('setFormValid', { formValid: this.isValid });
    },
};
</script>

<style scoped lang="scss">
@import "./src/style_variables/style_variables";
.lightspeed_configuration__form {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 32px;
    grid-auto-rows: min-content;
    padding: 12px;
}
.lightspeed_configuration__headline {
    margin-top: 0px;
}
.lightspeed_configuration__text-body {
    margin: 8px 0px;
}
.error {
    @extend %body1_style;
}
.error__headline {
    color: $red;
    margin-bottom: 24px;
}
.lightspeed_configuration__widget-example {
    margin-top: 12px;
}
.widgets {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 32px;
    grid-auto-rows: min-content;
}
</style>
